import type {
  LoginResponse,
  MfaSetupResponse,
  TokenResponse,
  TokenResponseWithSecurity,
  DashboardStats,
  ReferralStats,
  Referral,
  Commission,
  Payout,
  AffiliateProfile,
  StripeStatus,
  PromoMaterial,
  Activity,
  GhlStatus,
  GhlSettings,
  GhlPipeline,
  GhlCustomField,
  GhlLocation,
  LeaderboardEntry,
  AffiliateRank,
  NotificationPreferences,
  DeviceInfo,
} from '../types';

const RAW_URL = process.env.NEXT_PUBLIC_BACKEND_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
const API_BASE_URL = RAW_URL.endsWith('/api') ? RAW_URL : `${RAW_URL}/api`;

class AffiliateService {
  private accessToken: string | null = null;

  // ============================================
  // DEVICE FINGERPRINTING HELPER
  // ============================================

  private getDeviceInfo(): DeviceInfo {
    if (typeof window === 'undefined') {
      return { userAgent: 'server' };
    }
    return {
      userAgent: navigator.userAgent,
      acceptLanguage: navigator.language || navigator.languages?.join(','),
      timezoneOffset: new Date().getTimezoneOffset(),
    };
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  setAccessToken(token: string): void {
    this.accessToken = token;
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('affiliate_access_token', token);
    }
  }

  getAccessToken(): string | null {
    if (this.accessToken) return this.accessToken;
    if (typeof window !== 'undefined') {
      this.accessToken = sessionStorage.getItem('affiliate_access_token');
    }
    return this.accessToken;
  }

  clearSession(): void {
    this.accessToken = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('affiliate_access_token');
    }
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // ============================================
  // HTTP HELPERS
  // ============================================

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      this.clearSession();
      if (typeof window !== 'undefined') {
        window.location.href = '/affiliate/login';
      }
      throw new Error('Session expired');
    }

    const raw = await response.json();

    if (!response.ok) {
      // Error responses may also be wrapped in envelope
      const errPayload = raw.data || raw;
      throw new Error(errPayload.message || raw.message || 'Request failed');
    }

    // Backend wraps responses in { statusCode, data, timestamp } envelope — unwrap
    return raw.data !== undefined ? raw.data : raw;
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  async signup(data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    companyName?: string;
    websiteUrl?: string;
    promotionalBio?: string;
    socialLinks?: Record<string, string>;
    usesGhl?: boolean;
    turnstileToken?: string;
  }): Promise<{ success: boolean; message: string; affiliateId: string }> {
    return this.request('/v1/affiliate/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(email: string, password: string, turnstileToken?: string): Promise<LoginResponse> {
    return this.request('/v1/affiliate/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    });
  }

  async setupMfa(tempToken: string): Promise<MfaSetupResponse> {
    return this.request('/v1/affiliate/auth/mfa/setup', {
      method: 'POST',
      body: JSON.stringify({ tempToken }),
    });
  }

  async verifyMfaSetup(tempToken: string, code: string): Promise<TokenResponseWithSecurity> {
    const deviceInfo = this.getDeviceInfo();
    const response = await this.request<TokenResponseWithSecurity>('/v1/affiliate/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ tempToken, code, deviceInfo }),
    });

    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }

    return response;
  }

  async verifyMfaLogin(tempToken: string, code: string): Promise<TokenResponseWithSecurity> {
    const deviceInfo = this.getDeviceInfo();
    const response = await this.request<TokenResponseWithSecurity>('/v1/affiliate/auth/mfa/login', {
      method: 'POST',
      body: JSON.stringify({ tempToken, code, deviceInfo }),
    });

    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }

    return response;
  }

  async refreshSession(): Promise<TokenResponse> {
    const response = await this.request<TokenResponse>('/v1/affiliate/auth/refresh', {
      method: 'POST',
    });

    if (response.accessToken) {
      this.setAccessToken(response.accessToken);
    }

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/v1/affiliate/auth/logout', { method: 'POST' });
    } finally {
      this.clearSession();
    }
  }

  async getSession(): Promise<{
    success: boolean;
    affiliate: AffiliateProfile;
    session: { sessionId: string; issuedAt: number; expiresAt: number };
  }> {
    return this.request('/v1/affiliate/auth/session');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // ============================================
  // DASHBOARD
  // ============================================

  async getDashboard(): Promise<{ success: boolean; data: DashboardStats }> {
    return this.request('/v1/affiliate/dashboard');
  }

  async getStatsOverTime(
    startDate?: string,
    endDate?: string,
    groupBy?: 'day' | 'week' | 'month'
  ): Promise<{ success: boolean; data: ReferralStats[] }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (groupBy) params.append('groupBy', groupBy);

    return this.request(`/v1/affiliate/dashboard/stats?${params.toString()}`);
  }

  async getRecentReferrals(limit?: number): Promise<{ success: boolean; data: Referral[] }> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request(`/v1/affiliate/referrals/recent${params}`);
  }

  // ============================================
  // COMMISSIONS & PAYOUTS
  // ============================================

  async getCommissions(
    page?: number,
    limit?: number,
    status?: string
  ): Promise<{
    success: boolean;
    data: Commission[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (status) params.append('status', status);

    return this.request(`/v1/affiliate/commissions?${params.toString()}`);
  }

  async getPayouts(): Promise<{ success: boolean; data: Payout[] }> {
    return this.request('/v1/affiliate/payouts');
  }

  // ============================================
  // PROFILE
  // ============================================

  async getProfile(): Promise<{ success: boolean; data: AffiliateProfile }> {
    return this.request('/v1/affiliate/profile');
  }

  async updateProfile(data: Partial<AffiliateProfile>): Promise<{ success: boolean; message: string; data: Partial<AffiliateProfile> }> {
    return this.request('/v1/affiliate/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async setCustomSlug(slug: string): Promise<{ success: boolean; message: string; data: { customSlug: string; referralLink: string } }> {
    return this.request('/v1/affiliate/profile/slug', {
      method: 'POST',
      body: JSON.stringify({ slug }),
    });
  }

  // ============================================
  // STRIPE CONNECT
  // ============================================

  async getStripeStatus(): Promise<{ success: boolean; data: StripeStatus }> {
    return this.request('/v1/affiliate/stripe/status');
  }

  async startStripeOnboarding(): Promise<{ success: boolean; data: { onboardingUrl: string } }> {
    return this.request('/v1/affiliate/stripe/onboard', { method: 'POST' });
  }

  async getStripeDashboard(): Promise<{ success: boolean; data: { dashboardUrl: string } }> {
    return this.request('/v1/affiliate/stripe/dashboard');
  }

  // ============================================
  // PAYMENT METHOD
  // ============================================

  async getPaymentMethod(): Promise<{ success: boolean; data: import('../types').PaymentMethodData }> {
    return this.request('/v1/affiliate/payment-method');
  }

  async updatePaymentMethod(data: {
    paymentMethod: string;
    paymentEmail?: string;
    paymentHandle?: string;
    paymentBankDetails?: { bankName: string; routingNumber: string; accountNumber: string; accountHolderName: string };
    paymentMailingAddress?: string;
  }): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/payment-method', { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePaymentMethod(): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/payment-method', { method: 'DELETE' });
  }

  // ============================================
  // PROMOTIONAL MATERIALS
  // ============================================

  async getPromoMaterials(): Promise<{ success: boolean; data: PromoMaterial[] }> {
    return this.request('/v1/affiliate/promo-materials');
  }

  async trackPromoUse(materialId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/v1/affiliate/promo-materials/${materialId}/use`, { method: 'POST' });
  }

  // ============================================
  // ACTIVITY
  // ============================================

  async getActivityLog(limit?: number): Promise<{ success: boolean; data: Activity[] }> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request(`/v1/affiliate/activity${params}`);
  }

  // ============================================
  // GHL (GoHighLevel) INTEGRATION
  // ============================================

  async getGhlStatus(): Promise<{ success: boolean; data: GhlStatus }> {
    return this.request('/v1/affiliate/ghl/status');
  }

  async getGhlConnectUrl(): Promise<{ success: boolean; data: { authUrl: string } }> {
    return this.request('/v1/affiliate/ghl/connect');
  }

  async disconnectGhl(): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/ghl/disconnect', { method: 'DELETE' });
  }

  async saveGhlPrivateToken(token: string, locationId?: string): Promise<any> {
    return this.request('/v1/affiliate/ghl/private-token', {
      method: 'POST',
      body: JSON.stringify({ token, locationId }),
    });
  }

  async testGhlPrivateToken(token: string): Promise<any> {
    return this.request('/v1/affiliate/ghl/private-token/test', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async disconnectGhlPrivateToken(): Promise<any> {
    return this.request('/v1/affiliate/ghl/private-token', { method: 'DELETE' });
  }

  async updateGhlSettings(settings: GhlSettings): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/ghl/settings', {
      method: 'POST', // Backend expects POST, not PUT
      body: JSON.stringify(settings),
    });
  }

  async getGhlPipelines(): Promise<{ success: boolean; data: GhlPipeline[] }> {
    return this.request('/v1/affiliate/ghl/pipelines');
  }

  async getGhlCustomFields(): Promise<{ success: boolean; data: GhlCustomField[] }> {
    return this.request('/v1/affiliate/ghl/custom-fields');
  }

  async getGhlLocations(): Promise<{ success: boolean; data: GhlLocation[] }> {
    return this.request('/v1/affiliate/ghl/locations');
  }

  async switchGhlLocation(locationId: string): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/ghl/locations/active', {
      method: 'PUT', // Correct endpoint and method
      body: JSON.stringify({ locationId }),
    });
  }

  async triggerGhlSync(): Promise<{ success: boolean; message: string }> {
    return this.request('/v1/affiliate/ghl/sync', { method: 'POST' });
  }

  async getGhlWebhookConfig(): Promise<{
    success: boolean;
    data: {
      webhookUrl: string;
      webhookSecret: string;
      events: string[];
    };
  }> {
    return this.request('/v1/affiliate/ghl/webhook-config');
  }

  async regenerateGhlWebhookSecret(): Promise<{
    success: boolean;
    message: string;
    data: {
      webhookSecret: string;
    };
  }> {
    return this.request('/v1/affiliate/ghl/webhook-secret/regenerate', { method: 'POST' });
  }

  // ============================================
  // LEADERBOARD
  // ============================================

  async getLeaderboard(
    period: 'week' | 'month' | 'all' = 'month',
    limit: number = 10
  ): Promise<{ success: boolean; data: LeaderboardEntry[] }> {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('limit', limit.toString());

    return this.request(`/v1/affiliate/leaderboard?${params.toString()}`);
  }

  async getMyRank(
    period: 'week' | 'month' | 'all' = 'all'
  ): Promise<{ success: boolean; data: AffiliateRank }> {
    const params = new URLSearchParams();
    params.append('period', period);

    return this.request(`/v1/affiliate/rank?${params.toString()}`);
  }

  // ============================================
  // NOTIFICATIONS
  // ============================================

  async getNotificationPreferences(): Promise<{ success: boolean; data: NotificationPreferences }> {
    return this.request('/v1/affiliate/notifications');
  }

  async updateNotificationPreferences(
    preferences: Partial<NotificationPreferences>
  ): Promise<{ success: boolean; message: string; data: NotificationPreferences }> {
    return this.request('/v1/affiliate/notifications', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // ============================================
  // QR CODE
  // ============================================

  async getReferralQrCode(): Promise<{ success: boolean; data: { qrCode: string } }> {
    return this.request('/v1/affiliate/referral-qr');
  }
}

// Export singleton instance
export const affiliateService = new AffiliateService();
export default affiliateService;
